import { useCallback, useState } from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { ChevronsUpDown } from 'lucide-react';

import { _t } from '@/i18n/t';
import { APP_ROUTES } from '@/app/routePaths';
import { cn } from '@/shared/lib/utils';
import { DropdownMenu, DropdownMenuTrigger } from '@/shared/components/ui/dropdown-menu';
import { useActiveCompany } from '@/shared/state/ActiveCompanyContext';
import { isCompanyEditPath } from '@/shared/utils/activeCompany';

import CompanyInitialsBadge from './CompanyInitialsBadge';
import SidebarCompanyMenu from './SidebarCompanyMenu';

type SidebarCompanySelectorProps = {
  isCollapsed: boolean;
  onExpandSidebar: () => void;
};

const SidebarCompanySelector = ({ isCollapsed, onExpandSidebar }: SidebarCompanySelectorProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { companies, activeCompanyId, activeCompany, setActiveCompanyId, isLoading } = useActiveCompany();

  const handleSelectCompany = useCallback((id: number) => {
    setActiveCompanyId(id);
    if (isCompanyEditPath(pathname, APP_ROUTES.COMPANIES)) {
      navigate(APP_ROUTES.companyEdit(id));
    } else if (matchPath(APP_ROUTES.KB_SOURCE_PATH, pathname)) {
      navigate(APP_ROUTES.COMPANY_KNOWLEDGE_BASE);
    }
  }, [setActiveCompanyId, pathname, navigate]);

  const activeName = activeCompany?.label ?? '';

  if (isLoading) {
    return (
      <div className={cn('pb-2', isCollapsed ? 'flex justify-center' : 'px-2')}>
        <div
          aria-label={_t('sidebar.company.loading')}
          className={cn(
            'animate-pulse rounded-lg bg-secondary',
            isCollapsed ? 'h-11 w-11' : 'h-9 w-full',
          )}
        />
      </div>
    );
  }

  if (isCollapsed) {
    const collapsedLabel = activeName || _t('sidebar.company.none');
    return (
      <div className="flex justify-center pb-2">
        <button
          type="button"
          onClick={onExpandSidebar}
          aria-label={collapsedLabel}
          title={collapsedLabel}
          className="flex h-11 w-11 items-center justify-center rounded-lg bg-transparent transition-colors hover:bg-secondary"
        >
          <CompanyInitialsBadge name={activeName} />
        </button>
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="px-2 pb-2">
        <div
          className={cn(
            'flex items-center gap-2 rounded-lg p-1 transition-colors hover:bg-secondary',
            menuOpen && 'bg-secondary',
          )}
        >
          <span className="flex-1 truncate text-body text-nav-heading">
            {_t('sidebar.company.none')}
          </span>
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={_t('sidebar.company.switch')}
                title={_t('sidebar.company.switch')}
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-transparent text-nav-heading transition-colors hover:text-nav-iconHover"
              >
                <ChevronsUpDown className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <SidebarCompanyMenu
              companies={companies}
              activeCompanyId={activeCompanyId}
              onSelectCompany={handleSelectCompany}
              onAddCompany={() => navigate(APP_ROUTES.COMPANY_NEW)}
            />
          </DropdownMenu>
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 pb-2">
      <div
        className={cn(
          'flex items-center gap-2 rounded-lg p-1 transition-colors hover:bg-secondary',
          menuOpen && 'bg-secondary',
        )}
      >
        <Link
          to={activeCompanyId === null ? APP_ROUTES.COMPANY_NEW : APP_ROUTES.companyEdit(activeCompanyId)}
          title={_t('sidebar.company.edit', { name: activeName })}
          className="group flex min-w-0 flex-1 items-center gap-2"
        >
          <CompanyInitialsBadge name={activeName} />
          <span className="truncate text-body font-medium text-nav-textStrong transition-colors group-hover:text-primary">
            {activeName}
          </span>
        </Link>

        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={_t('sidebar.company.switch')}
              title={_t('sidebar.company.switch')}
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-transparent text-nav-heading transition-colors hover:text-nav-iconHover"
            >
              <ChevronsUpDown className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <SidebarCompanyMenu
            companies={companies}
            activeCompanyId={activeCompanyId}
            onSelectCompany={handleSelectCompany}
            onAddCompany={() => navigate(APP_ROUTES.COMPANY_NEW)}
          />
        </DropdownMenu>
      </div>
    </div>
  );
};

export default SidebarCompanySelector;
